import nookies from "nookies";
import * as Sentry from "@sentry/nextjs";

import { photosApi, BASE_API_URL, axiosInstance } from "src/services/api";
import { isTerminal, type PhotoMetadata } from "./metadata-flusher";
import { inAppBrowserTag } from "./in-app-tag";

/**
 * The wiring between the metadata flusher and the network.
 *
 * Extracted out of startUploadWithOrderPhoto so it can be tested at all. The
 * three callbacks below carry the whole durability story -- auth on the
 * page-hide beacon, requeue-on-beacon-failure, the axios error shape the
 * flusher's terminal-400 check depends on -- and they used to sit inline in a
 * 1700-line component with no test able to reach them.
 */
export interface MetadataTransport {
  send: (batch: PhotoMetadata[]) => Promise<void>;
  onError: (error: unknown) => void;
  beacon: (batch: PhotoMetadata[]) => Promise<void>;
}

/**
 * The `Authorization` header value to put on a raw fetch, or undefined.
 *
 * A logged-in customer's bearer token never reaches a bare `fetch`: it lives
 * either on the axios instance's shared defaults (set by `setBearerToken` in
 * services/api.ts) or on the `bearerToken` cookie that src/axios.ts's request
 * interceptor reads. Both are checked -- which one is populated depends on
 * whether the customer logged in this session or arrived with a cookie.
 *
 * Returns the complete header value (`Bearer x`), because the axios default is
 * already stored in that form. Note api.ts writes `false`, not undefined, when
 * clearing it, so a truthy-string check is what separates set from cleared.
 */
export function bearerHeader(): string | undefined {
  const fromAxios = axiosInstance?.defaults?.headers?.common?.["Authorization"];
  if (typeof fromAxios === "string" && fromAxios.length > 0) return fromAxios;

  const fromCookie = nookies.get(null).bearerToken;
  if (fromCookie) return `Bearer ${fromCookie}`;

  return undefined;
}

/**
 * Largest body we hand to one fetch(keepalive).
 *
 * Browsers refuse a keepalive request once the in-flight keepalive bodies of
 * the page exceed 64 KiB, and refusing means rejecting the WHOLE request. At
 * ~250 bytes per photo a backlog of a few hundred went over, so the beacon --
 * the one thing meant to save those photos on page hide -- failed outright for
 * exactly the large uploads that needed it. 48 KiB, not just under 64: the
 * quota is shared with everything else keepalive on a closing page -- Sentry's
 * fetch transport, analytics beacons -- and a first part refused for want of
 * 4 KiB would lose the only part that goes out. The sweep covers the rest.
 */
export const KEEPALIVE_BODY_LIMIT = 48 * 1024;

const encoder = typeof TextEncoder !== "undefined" ? new TextEncoder() : null;
const byteLength = (text: string): number =>
  // Worst case 3 bytes per UTF-16 unit when TextEncoder is missing: errs small.
  encoder ? encoder.encode(text).length : text.length * 3;

/**
 * Split a batch into request bodies that each serialise under `maxBytes`.
 *
 * Measured, not counted: names and keys vary in length, and non-ASCII names
 * (č, ž, ś, ł) take two bytes each. Order is preserved.
 */
export function splitForKeepalive(
  batch: PhotoMetadata[],
  maxBytes: number = KEEPALIVE_BODY_LIMIT,
): PhotoMetadata[][] {
  const envelope = byteLength(JSON.stringify({ metadata: [] }));
  const parts: PhotoMetadata[][] = [];
  let current: PhotoMetadata[] = [];
  let size = envelope;
  for (const item of batch) {
    // +1 for the separating comma; harmless overcount on the first item.
    const itemBytes = byteLength(JSON.stringify(item)) + 1;
    if (current.length > 0 && size + itemBytes > maxBytes) {
      parts.push(current);
      current = [];
      size = envelope;
    }
    current.push(item);
    size += itemBytes;
  }
  if (current.length > 0) parts.push(current);
  return parts;
}

export function createMetadataTransport(args: {
  orderId: string;
  orderPhotoId: string;
}): MetadataTransport {
  const { orderId, orderPhotoId } = args;

  return {
    /**
     * Deliberately lets the axios error propagate untouched.
     *
     * The flusher's isTerminal() reads `error.response.status`, so wrapping or
     * re-throwing a plain Error here would hide the 400 and silently turn the
     * terminal-drop path into an infinite retry path.
     */
    send: async (batch: PhotoMetadata[]) => {
      await photosApi.photoControllerUploadMetadataBatch({ metadata: batch });
    },

    onError: (error: unknown) => {
      // Severity tracks whether anything was actually lost. A terminal 400 is
      // the order refusing a write it is entitled to refuse -- expected, and
      // noise at `error` level. Anything else reaching onError means the
      // flusher gave up and those photos will never be registered, which is a
      // real loss of a customer's paid-for prints and belongs at `error`.
      const terminal = isTerminal(error);
      Sentry.captureException(error, {
        level: terminal ? "warning" : "error",
        tags: {
          area: "photo-upload",
          stage: "metadata-flush",
          terminal: String(terminal),
        },
        extra: { orderId, orderPhotoId },
      });
    },

    /**
     * Page-hide path: keepalive so the request outlives the page.
     *
     * `navigator.sendBeacon` is not usable here -- it cannot set the auth
     * headers this endpoint requires -- so this is fetch(keepalive) instead.
     *
     * Rejects on any failure so the flusher can put the batch back. A 401 or a
     * 500 RESOLVES a fetch, so checking `res.ok` is the only thing that
     * distinguishes "delivered" from "silently thrown away".
     */
    beacon: (batch: PhotoMetadata[]) => {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      // Without this a logged-in customer's beacon carried no credential at
      // all and JwtOrGuestAuthGuard 401'd every one of them.
      const authorization = bearerHeader();
      if (authorization) headers["Authorization"] = authorization;

      // Omitted entirely when absent. Sending `""` is not neutral: the guard
      // treats an empty string as falsy on both credentials, so a guest header
      // with no value is indistinguishable from no credential and 401s.
      const guestToken = nookies.get(null).guest_token;
      if (guestToken) headers["x-guest-token"] = guestToken;

      const sendPart = (part: PhotoMetadata[]): Promise<void> =>
        fetch(`${BASE_API_URL}/photos/uploadMetadataBatch`, {
          method: "POST",
          keepalive: true,
          credentials: "include",
          headers,
          body: JSON.stringify({ metadata: part }),
        }).then(
          (res) => {
            if (res.ok) return;
            // A non-OK response is a server that heard us and refused. That is
            // never ordinary background/foreground noise, so it is reported --
            // and rethrown, so the flusher requeues rather than dropping.
            const error = Object.assign(
              new Error(`Metadata beacon rejected (HTTP ${res.status})`),
              { response: { status: res.status } },
            );
            Sentry.captureException(error, {
              level: "error",
              tags: {
                area: "photo-upload",
                stage: "metadata-beacon",
                inAppBrowser: inAppBrowserTag(),
              },
              extra: { orderId, orderPhotoId, count: part.length, status: res.status },
            });
            throw error;
          },
          (error: unknown) => {
            // Network-level rejection. visibilitychange also fires on an
            // ordinary app switch, and a genuinely closing page cancelling its
            // own request looks identical from here -- so this stays at
            // `warning` to keep it out of the error budget, while still being
            // rethrown so the batch goes back on a page that survives.
            Sentry.captureException(error, {
              level: "warning",
              tags: {
                area: "photo-upload",
                stage: "metadata-beacon",
                inAppBrowser: inAppBrowserTag(),
              },
              extra: { orderId, orderPhotoId, count: part.length },
            });
            throw error;
          },
        );

      // One part after another, never all at once: the 64 KiB cap is on the
      // keepalive bodies in flight TOGETHER, so parallel parts would refuse
      // each other. The first goes out synchronously, inside the hide handler,
      // because on a page that is really closing nothing after it may run. On
      // a page that survives (an ordinary app switch) the rest follow. Any
      // failure rejects the whole beacon and the flusher requeues everything
      // it claimed; re-sending parts that did land is harmless (idempotent).
      const parts = splitForKeepalive(batch);
      if (parts.length === 0) return Promise.resolve();
      let chain = sendPart(parts[0]);
      for (const part of parts.slice(1)) {
        chain = chain.then(() => sendPart(part));
      }
      return chain;
    },
  };
}
